import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { getPropertyConfig } from '../utils/auth';
import { Card } from '../components/ui/card';
import { ShoppingCart, Package, FileText, Archive, DollarSign, Clock, AlertTriangle, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const propConfig = getPropertyConfig();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  const hasPO = propConfig?.has_po !== false;

  const primaryCards = hasPO ? [
    {
      title: 'Monthly Spending',
      value: formatRupiah(stats?.total_spending_month || 0),
      subtitle: 'Bulan ini',
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      trend: '+12% dari bulan lalu'
    },
    {
      title: 'Outstanding Payments',
      value: formatRupiah(stats?.outstanding_amount || 0),
      subtitle: `${stats?.outstanding_count || 0} PO belum lunas`,
      icon: AlertTriangle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      title: 'Stock Value',
      value: formatRupiah(stats?.stock_value || 0),
      subtitle: 'Total nilai inventory',
      icon: Archive,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Pending Approvals',
      value: stats?.pending_approvals || 0,
      subtitle: 'Perlu di-review',
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
  ] : [
    {
      title: 'Total Items',
      value: stats?.total_items || 0,
      subtitle: 'Total stok di gudang',
      icon: Archive,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: hasPO ? 'Receiving' : 'Barang Masuk',
      value: stats?.total_receiving || 0,
      subtitle: `${stats?.open_receiving || 0} Open`,
      icon: Package,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: hasPO ? 'Store Requisitions' : 'Barang Keluar',
      value: stats?.total_srs || 0,
      subtitle: `${stats?.open_srs || 0} Open`,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Low Stock Items',
      value: stats?.low_stock_count || 0,
      subtitle: 'Perlu restock',
      icon: AlertTriangle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  const secondaryCards = hasPO ? [
    {
      title: 'Purchase Orders',
      value: stats?.total_pos || 0,
      subtitle: `${stats?.open_pos || 0} Open`,
      icon: ShoppingCart,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Receiving',
      value: stats?.total_receiving || 0,
      subtitle: `${stats?.open_receiving || 0} Open`,
      icon: Package,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Store Requisitions',
      value: stats?.total_srs || 0,
      subtitle: `${stats?.open_srs || 0} Open`,
      icon: FileText,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Avg Lead Time',
      value: `${stats?.avg_lead_time || 0} days`,
      subtitle: 'PO to Receiving',
      icon: TrendingUp,
      color: 'text-primary',
      bgColor: 'bg-accent',
    },
  ] : [];

  const chartData = hasPO ? [
    { name: 'PO', Total: stats?.total_pos || 0, Open: stats?.open_pos || 0 },
    { name: 'Receiving', Total: stats?.total_receiving || 0, Open: stats?.open_receiving || 0 },
    { name: 'SR', Total: stats?.total_srs || 0, Open: stats?.open_srs || 0 },
  ] : [
    { name: 'Barang Masuk', Total: stats?.total_receiving || 0, Open: stats?.open_receiving || 0 },
    { name: 'Barang Keluar', Total: stats?.total_srs || 0, Open: stats?.open_srs || 0 },
  ];

  const COLORS = ['#1A4D2E', '#2C6E49', '#4A7C59', '#77A87D'];

  return (
    <div className="space-y-8 fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-primary mb-2" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your inventory management system</p>
      </div>

      {/* Financial & Operational KPIs */}
      <div>
        <h2 className="text-lg font-semibold text-primary mb-4">{hasPO ? 'Financial & Operational Metrics' : 'Ringkasan Inventory'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {primaryCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="p-6 card-hover border-l-4"
                style={{ borderLeftColor: stat.color.replace('text-', '#') }}
                data-testid={`metric-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-primary mb-1">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
                  {stat.trend && (
                    <p className="text-xs text-green-600 mt-2">{stat.trend}</p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Operational Stats */}
      <div>
        <h2 className="text-lg font-semibold text-primary mb-4">Operational Stats</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {secondaryCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="p-6 card-hover"
                data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-primary mb-1">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Low Stock Alerts */}
      {stats?.low_stock_count > 0 && (
        <Card className="p-6 border-l-4 border-red-500 bg-red-50/30" data-testid="low-stock-alert">
          <div className="flex items-start gap-4">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                ⚠️ Low Stock Alert
              </h3>
              <p className="text-sm text-red-800 mb-3">
                {stats.low_stock_count} item(s) dibawah minimum stock level
              </p>
              <div className="space-y-2">
                {stats.low_stock_items?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{item.item_name}</p>
                      <p className="text-xs text-muted-foreground">{item.warehouse}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-red-600">
                        {item.current} / {item.minimum}
                      </p>
                      <p className="text-xs text-muted-foreground">Current / Min</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Charts and Top Suppliers */}
      <div className={`grid grid-cols-1 ${hasPO ? 'lg:grid-cols-2' : ''} gap-6`}>
        {/* Bar Chart */}
        <Card className="p-6" data-testid="overview-chart">
          <h3 className="text-lg font-semibold mb-4 text-primary">Overview</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
              <YAxis stroke="hsl(var(--muted-foreground))" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.5rem',
                }}
              />
              <Legend />
              <Bar dataKey="Total" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
              <Bar dataKey="Open" fill="hsl(var(--accent-foreground))" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Top Suppliers - only for PO properties */}
        {hasPO && (
        <Card className="p-6" data-testid="top-suppliers">
          <h3 className="text-lg font-semibold mb-4 text-primary">Top Suppliers</h3>
          {stats?.top_suppliers && stats.top_suppliers.length > 0 ? (
            <div className="space-y-3">
              {stats.top_suppliers.map((supplier, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-accent/30 hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm">
                      {idx + 1}
                    </div>
                    <p className="font-medium">{supplier.name}</p>
                  </div>
                  <p className="text-sm font-semibold text-primary">
                    {formatRupiah(supplier.total)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No supplier data available</p>
          )}
        </Card>
        )}
      </div>

      {/* Recent Activities */}
      <Card className="p-6" data-testid="recent-activities">
        <h3 className="text-lg font-semibold mb-4 text-primary">Recent Activities</h3>
        <div className="space-y-4">
          {hasPO && stats?.recent_pos?.slice(0, 3).map((po, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-accent/30 hover:bg-accent/50 transition-colors">
              <div className="p-2 rounded-lg bg-primary/10">
                <ShoppingCart className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{po.po_number}</p>
                <p className="text-xs text-muted-foreground">{po.supplier_name}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-primary">{formatRupiah(po.total_amount)}</p>
                <span className={`status-badge status-${po.status.toLowerCase()}`}>
                  {po.status}
                </span>
              </div>
            </div>
          ))}
          {stats?.recent_receiving?.slice(0, hasPO ? 2 : 5).map((rcv, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-accent/30 hover:bg-accent/50 transition-colors">
              <div className="p-2 rounded-lg bg-green-100">
                <Package className="h-4 w-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{rcv.receiving_number}</p>
                <p className="text-xs text-muted-foreground">{rcv.location}</p>
              </div>
              <span className={`status-badge status-${rcv.status.toLowerCase()}`}>
                {rcv.status}
              </span>
            </div>
          ))}
          {(!stats?.recent_receiving?.length && !stats?.recent_pos?.length) && (
            <p className="text-center text-muted-foreground py-4">Belum ada aktivitas</p>
          )}
        </div>
      </Card>
    </div>
  );
}
